// AppHeader — barra superior común (60px) compartida por los layouts con
// sidebar (RoleLayout) y los de top-nav (DirectivoLayout):
//
//   Wordmark + divisor + roleLabel ........ [extra] [theme] [logout] [avatar]
//
// Extraído de RoleLayout para que el layout del directivo (top-nav, sin
// sidebar) reuse el mismo header sin duplicar el markup ni los íconos.

import type { ReactNode } from 'react';
import { Icon } from '../ui/icon';
import { Avatar } from '../ui/avatar';
import { useAuth } from '../../lib/auth';
import { useTheme } from '../../lib/theme/use-theme';

export function AppHeader({
  roleLabel,
  extra,
  onMenuToggle,
}: {
  roleLabel?: string;
  extra?: ReactNode;
  /** Si se pasa, muestra el botón hamburguesa (drawer del sidebar en mobile). */
  onMenuToggle?: () => void;
}) {
  const { state, signOut } = useAuth();
  const { theme, toggle } = useTheme();

  if (state.kind !== 'signed_in') return null;
  const claims = state.claims;
  const initials =
    claims.email
      .split('@')[0]
      ?.split(/[.\-_]/)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('')
      .slice(0, 2) ?? 'U';

  return (
    <header className="flex h-[60px] flex-shrink-0 items-center gap-3 border-b border-border bg-surface-base px-4 md:px-6">
      {onMenuToggle ? (
        <button
          type="button"
          onClick={onMenuToggle}
          className="-ml-1 rounded p-1.5 text-fg-secondary transition-colors hover:bg-surface-overlay"
          aria-label="Abrir menú"
        >
          <Icon name="menu" size={20} stroke={1.5} />
        </button>
      ) : null}
      <Wordmark />
      <Divider />
      {roleLabel ? (
        <span className="hidden text-xs font-medium uppercase tracking-[0.08em] text-fg-tertiary md:inline">
          {roleLabel}
        </span>
      ) : null}
      <div className="flex-1" />

      {extra}

      {/* Theme toggle */}
      <button
        type="button"
        onClick={toggle}
        className="rounded p-1.5 text-fg-secondary transition-colors hover:bg-surface-overlay"
        aria-label={`Cambiar a tema ${theme === 'light' ? 'oscuro' : 'claro'}`}
        title={`Tema ${theme === 'light' ? 'claro' : 'oscuro'} (click para alternar)`}
      >
        {theme === 'light' ? <SunIcon /> : <MoonIcon />}
      </button>

      {/* Logout */}
      <button
        type="button"
        onClick={() => signOut()}
        className="rounded p-1.5 text-fg-secondary transition-colors hover:bg-surface-overlay"
        aria-label="Cerrar sesión"
        title="Cerrar sesión"
      >
        <Icon name="logout" size={16} stroke={1.5} />
      </button>

      <Avatar initials={initials} size={28} />
    </header>
  );
}

export function Wordmark() {
  return (
    <span className="font-serif text-xl leading-none tracking-tight text-fg">
      Jidokap<span className="text-dot">.</span>
    </span>
  );
}

function Divider() {
  return <span aria-hidden className="h-4 w-px bg-border-strong" />;
}

function SunIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  );
}
